import React, { useState, useRef, useMemo } from 'react';
import {
  Upload, FileText, Search, Grid3x3, List, Eye, Download, Trash2, Clock, CheckCircle,
  Loader2, ArrowLeft, Sparkles, User, Brain, Search as SearchIcon, Activity, AlertCircle,
  Copy, Check
} from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { documentStoreService } from '@/services/document-store.service';
import { DocumentStore, DocumentUrl, DocumentProgressStatusEnum } from '@/types/document-store.types';
import { downloadFile } from '@/utils/downloadFile';
import { getFileTypeInfo } from '@/utils/fileTypeIconUtils';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import ReactSelect, { StylesConfig } from 'react-select';
import UploadDocumentModal from '@/components/knowledge/UploadDocumentModal';
import DocumentPreviewModal from '@/components/knowledge/DocumentPreviewModal';
import AIDocumentSearchModal from '@/components/knowledge/AIDocumentSearchModal';
import StatusIndicator, { ProcessingProgress } from '@/components/knowledge/StatusIndicator';
import { useRealTimeStatus } from '@/hooks/useRealTimeStatus';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import useHasPermission from '@/hooks/useHasPermission';
import { DOCUMENT_STORE_PERMISSION } from '@/constants/constant-permissions';

interface DocumentUI {
  id: string;
  name: string;
  key: string;
  type: string;
  size: string;
  uploadedAt: string;
  uploadedBy: string;
  status: DocumentProgressStatusEnum | 'uploading' | 'queued';
  processingProgress: number;
  isExample: boolean;
  url?: string;
  remarks?: string; // failure reason (shown when status is FAILED)
  summary?: string; // AI-generated document summary (present once processing is DONE)
  pageCount?: number;
}

interface SelectOption {
  label: string;
  value: string;
}

// Custom styles for React Select (matching Select.tsx)
const customSelectStyles: StylesConfig<SelectOption, false> = {
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused ? '#3b82f6' : '#e5e7eb',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
    '&:hover': {
      borderColor: '#3b82f6',
    },
    minHeight: '42px',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#3b82f6'
      : state.isFocused
      ? '#dbeafe'
      : 'white',
    color: state.isSelected ? 'white' : '#1f2937',
    cursor: 'pointer',
    fontSize: '0.875rem',
    '&:active': {
      backgroundColor: '#2563eb',
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    zIndex: 50,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '0.25rem',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#9ca3af',
    fontSize: '0.875rem',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#1f2937',
    fontSize: '0.875rem',
  }),
};

// Filter and Sort options
const statusFilterOptions: SelectOption[] = [
  { label: 'All Status', value: 'all' },
  { label: 'Completed', value: 'completed' },
  { label: 'Processing', value: 'processing' },
  { label: 'Error', value: 'error' },
];

const sortByOptions: SelectOption[] = [
  { label: 'Sort by Date', value: 'date' },
  { label: 'Sort by Name', value: 'name' },
  { label: 'Sort by Status', value: 'status' },
];

export default function DocumentStoreDetail() {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'completed' | 'processing' | 'error'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'status'>('date');
  const [dragOver, setDragOver] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentUrl | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<{ id: string; name: string } | null>(null);
  // Document ids whose AI summary is expanded to its full text. Collapsed by
  // default (clamped to a few lines) so a long summary doesn't blow out the card
  // grid, but the complete text is always one click away.
  const [expandedSummaries, setExpandedSummaries] = useState<Set<string>>(new Set());

  const toggleSummary = (docId: string) => {
    setExpandedSummaries(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  // Which summary was just copied — drives the transient tick on the copy button.
  const [copiedSummaryId, setCopiedSummaryId] = useState<string | null>(null);

  const copySummary = async (docId: string, summary?: string) => {
    if (!summary) return;
    try {
      await navigator.clipboard.writeText(summary);
      setCopiedSummaryId(docId);
      toast.success('Summary copied');
      setTimeout(() => {
        // Only clear if this doc is still the copied one, so a later copy of a
        // different summary isn't reset by an earlier timer.
        setCopiedSummaryId(current => (current === docId ? null : current));
      }, 2000);
    } catch {
      // navigator.clipboard is unavailable on insecure origins / older browsers.
      toast.error('Could not copy — your browser blocked clipboard access');
    }
  };
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canWrite = useHasPermission([DOCUMENT_STORE_PERMISSION.ADMIN_DOCUMENT_STORE_WRITE]);
  const queryClient = useQueryClient();
  
  const { store, processingStats, isLoading } = useRealTimeStatus({
    storeId: storeId!,
    enabled: !!storeId,
    pollInterval: 5000, // Poll every 5 seconds
  });

  const toggleExampleMutation = useMutation({
    mutationFn: ({ docId, useAsExample }: { docId: string, useAsExample: boolean }) => 
      documentStoreService.updateDocumentExampleStatus(storeId!, docId, useAsExample),
    onMutate: async ({ docId, useAsExample }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['document-store', storeId] });
      
      // Snapshot the previous value
      const previousStore = queryClient.getQueryData(['document-store', storeId]);
      
      // Optimistically update to the new value
      queryClient.setQueryData(['document-store', storeId], (old: DocumentStore | undefined) => {
        if (!old) return old;
        return {
          ...old,
          document_urls: old.document_urls.map(doc =>
            doc._id === docId ? { ...doc, useAsExample } : doc
          ),
        };
      });
      
      // Return a context with the previous and new value
      return { previousStore };
    },
    onSuccess: () => {
      toast.success('Document updated successfully!');
      // Invalidate to ensure data consistency
      queryClient.invalidateQueries({ queryKey: ['document-store', storeId] });
    },
    onError: (error: any, _, context) => {
      // If the mutation fails, use the context to roll back
      if (context?.previousStore) {
        queryClient.setQueryData(['document-store', storeId], context.previousStore);
      }
      toast.error(error.message || 'Failed to update document.');
    }
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: async (docId: string) => {
      // Remove document from store and save
      const updatedStore = await documentStoreService.removeDocumentFromStore(storeId!, docId);
      return updatedStore;
    },
    onMutate: async (docId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['document-store', storeId] });
      
      // Snapshot the previous value
      const previousStore = queryClient.getQueryData(['document-store', storeId]);
      
      // Optimistically update to the new value
      queryClient.setQueryData(['document-store', storeId], (old: DocumentStore | undefined) => {
        if (!old) return old;
        return {
          ...old,
          document_urls: old.document_urls.filter(doc => doc._id !== docId),
        };
      });
      
      // Return a context with the previous value
      return { previousStore };
    },
    onSuccess: () => {
      toast.success('Document deleted successfully!');
      // Invalidate to ensure data consistency
      queryClient.invalidateQueries({ queryKey: ['document-store', storeId] });
    },
    onError: (error: any, _, context) => {
      // If the mutation fails, use the context to roll back
      if (context?.previousStore) {
        queryClient.setQueryData(['document-store', storeId], context.previousStore);
      }
      toast.error(error.message || 'Failed to delete document.');
    }
  });

  const isDeleteDisabled = !canWrite || deleteDocumentMutation.isLoading;

  const documents: DocumentUI[] = useMemo(() => {
    if (!store) return [];
    
    let docs = store.document_urls.map(d => {
      // Convert MIME type to extension for display if needed
      let displayType = d.fileType;
      if (d.fileType && d.fileType.includes('/')) {
        // It's a MIME type, extract the extension for display
        const parts = d.fileType.split('/');
        displayType = parts[1] || d.fileType;
        // Handle special cases
        if (displayType === 'vnd.openxmlformats-officedocument.wordprocessingml.document') displayType = 'docx';
        if (displayType === 'vnd.openxmlformats-officedocument.spreadsheetml.sheet') displayType = 'xlsx';
        if (displayType === 'vnd.openxmlformats-officedocument.presentationml.presentation') displayType = 'pptx';
        if (displayType === 'vnd.ms-excel') displayType = 'xls';
        if (displayType === 'vnd.ms-powerpoint') displayType = 'ppt';
        if (displayType === 'msword') displayType = 'doc';
        if (displayType === 'jpeg') displayType = 'jpg';
        if (displayType === 'quicktime') displayType = 'mov';
        if (displayType === 'x-msvideo') displayType = 'avi';
        if (displayType === 'mpeg') displayType = 'mp3';
        if (displayType === 'x-wav') displayType = 'wav';
        if (displayType === 'x-m4a') displayType = 'm4a';
        if (displayType === 'plain') displayType = 'txt';
      }
      
      return {
        id: d._id,
        name: d.name,
        key: d.key,
        type: displayType,
        size: (() => {
        const size = Number(d.fileSize);
        if (isNaN(size) || size <= 0) return "N/A";

        // Show KB for files < 1MB, MB for files >= 1MB
        if (size < 1024 * 1024) {
          return `${(size / 1024).toFixed(2)} KB`;
        } else {
          return `${(size / (1024 * 1024)).toFixed(2)} MB`;
        }
        })(),
        uploadedAt: format(new Date(d.createdAt), 'PPpp'),
        uploadedBy: d.uploadedBy || 'System',
        status: d.status,
        processingProgress: d.status === DocumentProgressStatusEnum.DONE ? 100 : 0,
        isExample: d.useAsExample,
        url: d.url,
        remarks: d.remarks,
        summary: d.summary,
        pageCount: d.metadata?.page_count,
      };
    });

    // Filter
    if (filterStatus !== 'all') {
      const statusMap = {
        'completed': DocumentProgressStatusEnum.DONE,
        'processing': DocumentProgressStatusEnum.IN_PROGRESS,
        'error': DocumentProgressStatusEnum.FAILED,
      };
      docs = docs.filter(d => d.status === statusMap[filterStatus]);
    }

    // Search
    if (searchQuery) {
      docs = docs.filter(d => 
        d.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort
    docs.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'status':
          return a.status.localeCompare(b.status);
        case 'date':
        default:
          return new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime();
      }
    });

    return docs;
  }, [store, filterStatus, searchQuery, sortBy]);
const hasUploadedFiles = store?.document_urls && store.document_urls.length > 0;

  const getFileIcon = (type: string) => {
    return getFileTypeInfo(type).icon;
  };

  // Utility function to properly download files with original filename.
  // Passes document_store audit metadata so the backend records the download in the audit trail.
  const handleDownloadFile = async (url: string, filename: string, fileId?: string) => {
    const toastId = toast.loading('Preparing download...');
    try {
      await downloadFile(url, filename, {
        module: 'document_store',
        resource_id: fileId,
        resource_name: filename,
        parent_id: store?.id,
        parent_name: store?.name,
      });
      toast.success('File downloaded successfully', { id: toastId });
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file', { id: toastId });
    }
  };

  // Check if file type supports preview
  const supportsPreview = (fileType: string): boolean => {
    const type = fileType?.toLowerCase();

    // Handle MIME types
    if (type.includes('/')) {

      if (type === 'video/quicktime' || type === 'video/x-msvideo') {
        return false;
      }

      if (type.includes('pdf') || type.includes('image') || type.includes('video') || type.includes('audio')) {
        return true;
      }
      if (type.includes('text/plain')) return true;
      return false;
    }

    // Handle extensions
    const previewableTypes = ['pdf', 'txt', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'mp4', 'mp3', 'wav', 'm4a'];
    return previewableTypes.includes(type);
  };

  const handleDragOver = (e: React.DragEvent) => { 
    e.preventDefault(); 
    setDragOver(true); 
  };
  
  const handleDragLeave = (e: React.DragEvent) => { 
    e.preventDefault(); 
    setDragOver(false); 
  };
  
  const handleDrop = (e: React.DragEvent) => { 
    e.preventDefault(); 
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setShowUploadModal(true);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="bg-gradient-to-r from-brand-indigo-600 to-brand-purple-600 rounded-xl p-6 text-white">
          <div className="mb-4">
            <SkeletonLoader type="custom" className="h-6 w-32 rounded bg-white/20" />
          </div>

          <div className="flex items-start justify-between gap-6">
            <div className="flex-1 min-w-0">
              <SkeletonLoader type="custom" className="h-8 w-64 mb-2 rounded bg-white/20" />
              <SkeletonLoader type="custom" className="h-4 w-96 mb-4 rounded bg-white/20" />

              {/* Stats - Enhanced Card Style */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="bg-white/15 backdrop-blur-md rounded-xl p-3 border border-white/20">
                    <div className="flex items-center justify-between mb-2">
                      <SkeletonLoader type="custom" className="w-7 h-7 rounded-lg bg-white/20" />
                    </div>
                    <SkeletonLoader type="custom" className="h-8 w-12 mb-1 rounded bg-white/20" />
                    <SkeletonLoader type="custom" className="h-3 w-20 rounded bg-white/20" />
                  </div>
                ))}
              </div>

              {/* Progress Bar Skeleton */}
              <div className="mt-4">
                <SkeletonLoader type="custom" className="h-4 w-full rounded-full bg-white/20" />
              </div>
            </div>

            <div className="shrink-0">
              <SkeletonLoader type="custom" className="w-40 h-10 rounded-lg bg-white/20" />
            </div>
          </div>
        </div>

        {/* Toolbar Skeleton */}
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <SkeletonLoader type="custom" className="w-20 h-10 rounded-lg" />
              <SkeletonLoader type="custom" className="w-64 h-10 rounded-lg" />
            </div>
            <div className="flex items-center gap-2">
              <SkeletonLoader type="custom" className="w-32 h-10 rounded-lg" />
              <SkeletonLoader type="custom" className="w-32 h-10 rounded-lg" />
            </div>
          </div>
        </div>

        {/* AI Search Card Skeleton */}
        <div className="card bg-gradient-to-r from-brand-indigo-50 to-brand-purple-50">
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-4">
              <SkeletonLoader type="custom" className="w-12 h-12 rounded-lg" />
              <div>
                <SkeletonLoader type="text" className="h-5 w-64 mb-2" />
                <SkeletonLoader type="text" className="h-4 w-96" />
              </div>
            </div>
            <SkeletonLoader type="custom" className="w-32 h-10 rounded-lg" />
          </div>
        </div>

        {/* Upload Area Skeleton */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <SkeletonLoader type="custom" className="w-12 h-12 mx-auto mb-4 rounded" />
          <SkeletonLoader type="text" className="h-5 w-64 mx-auto mb-2" />
          <SkeletonLoader type="text" className="h-4 w-48 mx-auto mb-4" />
          <SkeletonLoader type="button" className="mx-auto" />
        </div>

        {/* Document Cards Grid Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3 flex-1">
                  <SkeletonLoader type="custom" className="w-5 h-5 rounded" />
                  <div className="min-w-0 flex-1">
                    <SkeletonLoader type="text" className="h-5 w-full mb-1" />
                    <SkeletonLoader type="text" className="h-4 w-20" />
                  </div>
                </div>
                <SkeletonLoader type="custom" className="w-20 h-6 rounded-full" />
              </div>

              {/* Example Toggle Skeleton */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-3">
                <div className="flex items-center gap-2">
                  <SkeletonLoader type="custom" className="w-4 h-4 rounded" />
                  <SkeletonLoader type="text" className="h-4 w-28" />
                </div>
                <SkeletonLoader type="custom" className="w-9 h-5 rounded-full" />
              </div>

              {/* Status Skeleton */}
              <div className="flex items-center justify-between mb-3">
                <SkeletonLoader type="custom" className="w-24 h-6 rounded-full" />
              </div>

              {/* Footer Skeleton */}
              <div className="flex items-center justify-between pt-3 border-t">
                <div className="space-y-2">
                  <SkeletonLoader type="text" className="h-3 w-24" />
                  <SkeletonLoader type="text" className="h-3 w-32" />
                </div>
                <div className="flex gap-1">
                  <SkeletonLoader type="custom" className="w-7 h-7 rounded" />
                  <SkeletonLoader type="custom" className="w-7 h-7 rounded" />
                  <SkeletonLoader type="custom" className="w-7 h-7 rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Document store not found</p>
        <button onClick={() => navigate('/admin/knowledge/documents')} className="mt-4 text-brand-indigo-600 hover:text-brand-indigo-700">
          Back to Documents
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-indigo-600 to-brand-purple-600 rounded-xl p-6 text-white">
        <div className="mb-4">
          <button
            onClick={() => navigate('/admin/knowledge/documents')}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Stores
          </button>
        </div>

        {/* Title and Upload Button */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold mb-2 truncate" title={store.name}>{store.name}</h1>
            <p className="text-brand-indigo-100 line-clamp-2 break-words" title={store.description}>{store.description}</p>
          </div>
          <div className="flex items-start shrink-0">
           <button
              onClick={() => {
                if (!canWrite) return;
                setShowUploadModal(true);
              }}
              disabled={!canWrite}
              title={!canWrite ? "You do not have permission" : undefined}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap
                ${
                  canWrite
                    ? "bg-white text-brand-indigo-600 hover:bg-brand-indigo-50 cursor-pointer"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed opacity-60"
                }`}
            >
              <Upload className="w-5 h-5" />
              <span className="hidden sm:inline">Upload Documents</span>
              <span className="sm:hidden">Upload</span>
            </button>
          </div>
        </div>

        {/* Stats - Enhanced Card Style - Full Width */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Total Documents */}
          <div className="bg-white/15 backdrop-blur-md rounded-xl p-3 border border-white/20 hover:bg-white/20 transition-all">
            <div className="flex items-center justify-between mb-2">
              <div className="p-1.5 bg-white/20 rounded-lg">
                <FileText className="w-4 h-4 text-white" />
              </div>
            </div>
            <p className="text-2xl font-bold text-white">{processingStats?.total || 0}</p>
            <p className="text-brand-indigo-100 text-xs font-medium">Total Documents</p>
          </div>

          {/* AI Processed */}
          <div className="bg-white/15 backdrop-blur-md rounded-xl p-3 border border-white/20 hover:bg-white/20 transition-all">
            <div className="flex items-center justify-between mb-2">
              <div className="p-1.5 bg-white/20 rounded-lg">
                <CheckCircle className="w-4 h-4 text-white" />
              </div>
              {processingStats?.total > 0 && (
                <span className="text-xs font-semibold text-white bg-white/20 px-2 py-0.5 rounded-full">
                  {Math.round((processingStats?.completed / processingStats?.total) * 100)}%
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-white">{processingStats?.completed || 0}</p>
            <p className="text-brand-indigo-100 text-xs font-medium">AI Processed</p>
          </div>

          {/* Processing */}
          <div className="bg-white/15 backdrop-blur-md rounded-xl p-3 border border-white/20 hover:bg-white/20 transition-all">
            <div className="flex items-center justify-between mb-2">
              <div className="p-1.5 bg-white/20 rounded-lg">
                <Activity className="w-4 h-4 text-white" />
              </div>
              {processingStats?.processing > 0 && (
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-300 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-yellow-200"></span>
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-white">{processingStats?.processing || 0}</p>
            <p className="text-brand-indigo-100 text-xs font-medium">Processing</p>
          </div>

          {/* Example Docs */}
          <div className="bg-white/15 backdrop-blur-md rounded-xl p-3 border border-white/20 hover:bg-white/20 transition-all">
            <div className="flex items-center justify-between mb-2">
              <div className="p-1.5 bg-white/20 rounded-lg">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
            </div>
            <p className="text-2xl font-bold text-white">{store?.document_urls.filter(d => d.useAsExample).length || 0}</p>
            <p className="text-brand-indigo-100 text-xs font-medium">Example Docs</p>
          </div>
        </div>

        {/* Processing Progress Bar */}
        {processingStats && processingStats.total > 0 && (
          <div className="mt-4">
            <ProcessingProgress
              total={processingStats.total}
              completed={processingStats.completed}
              processing={processingStats.processing}
              failed={processingStats.failed}
              showDetails={false}
            />
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* View Mode Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('card')}
                className={`p-2 rounded-md transition-colors ${viewMode === 'card' ? 'bg-white text-gray-900 shadow' : 'text-gray-500 hover:text-gray-700'}`}
                title="Card View"
              >
                <Grid3x3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white text-gray-900 shadow' : 'text-gray-500 hover:text-gray-700'}`}
                title="List View"
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-indigo-500 focus:border-brand-indigo-500 w-64"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Filter */}
            <div className="w-40">
              <ReactSelect
                value={statusFilterOptions.find(opt => opt.value === filterStatus)}
                onChange={(option) => setFilterStatus(option?.value as any)}
                options={statusFilterOptions}
                styles={customSelectStyles}
                isSearchable={false}
                placeholder="Filter Status"
              />
            </div>

            {/* Sort */}
            <div className="w-44">
              <ReactSelect
                value={sortByOptions.find(opt => opt.value === sortBy)}
                onChange={(option) => setSortBy(option?.value as any)}
                options={sortByOptions}
                styles={customSelectStyles}
                isSearchable={false}
                placeholder="Sort By"
              />
            </div>
          </div>
        </div>
      </div>

          {/* AI Document Search Card */}
          <div 
            className={`card bg-gradient-to-r from-brand-indigo-50 to-brand-purple-50 transition-all ${
              hasUploadedFiles 
                ? 'hover:from-brand-indigo-100 hover:to-brand-purple-100 cursor-pointer shadow-sm' 
                : 'opacity-50 cursor-not-allowed grayscale border-gray-200' 
            }`}
            onClick={() => {
              // Only open if there are files
              if (hasUploadedFiles) {
                setShowSearchModal(true);
              }
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg shadow-sm ${hasUploadedFiles ? 'bg-white' : 'bg-gray-100'}`}>
                  <Brain className={`w-6 h-6 ${hasUploadedFiles ? 'text-brand-indigo-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h3 className={`text-lg font-semibold mb-1 ${hasUploadedFiles ? 'text-gray-900' : 'text-gray-500'}`}>
                    AI-Powered Document Search
                  </h3>
                  <p className="text-sm text-gray-600">
                    {hasUploadedFiles 
                      ? "Search through your document content using natural language queries" 
                      : "Upload documents first to enable AI-powered searching"}
                  </p>
                </div>
              </div>
              
              <button 
                disabled={!hasUploadedFiles} // This natively disables the button
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors shadow-sm ${
                  hasUploadedFiles 
                    ? 'bg-white text-brand-indigo-600 hover:bg-brand-indigo-50' 
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <SearchIcon className="w-4 h-4" />
                Open Search
              </button>
            </div>
          </div>

      {/* Upload Area */}
      <div 
        onDragOver={handleDragOver} 
        onDragLeave={handleDragLeave} 
        onDrop={handleDrop} 
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragOver ? 'border-brand-indigo-400 bg-brand-indigo-50' : 'border-gray-300 hover:border-brand-indigo-400'
        }`}
      >
        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-lg font-medium text-gray-900 mb-2">Drop files here or click to upload</p>
        <div className="text-sm text-gray-500 mb-4">
        <p className="font-medium mb-1">Accepted File Types:</p>
        <p className="text-xs">
            📄 Documents: PDF, DOCX, DOC, PPTX, TXT, CSV<br/>
            🖼️ Images: JPG, PNG, GIF, WEBP <br/>
            🎥 Video: MP4, AVI, MOV<br/>
            🎵 Audio: MP3, WAV, M4A<br/>
            <span className="text-gray-400">Maximum file size: 10MB</span>
        </p>
        </div>
        <input ref={fileInputRef} type="file" multiple className="hidden" />
        <button onClick={() => canWrite && setShowUploadModal(true)} 
          disabled={!canWrite}
          title={!canWrite ? 'You do not have permission' : undefined}
          className={`btn btn-primary ${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}>
          Choose Files
        </button>
      </div>

      {/* Documents */}
      {viewMode === 'card' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc) => (
            <div key={doc.id} className="card hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3 flex-1 min-w-0 overflow-hidden">
                  <div className="shrink-0">
                    {getFileIcon(doc.type)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-gray-900 truncate" title={doc.name}>
                      {doc.name}
                    </h4>
                    <p className="text-sm text-gray-500">{doc.size}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <StatusIndicator status={doc.status} showLabel={false} showPulse={true} />
                </div>
              </div>

              {/* Example Toggle */}
              {!['csv', 'txt', 'mp3', 'wav', 'm4a'].includes(doc.type) && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-medium text-gray-700">Use as Example</span>
                  {doc.isExample && (
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                      Active
                    </span>
                  )}
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={doc.isExample}
                    onChange={() => toggleExampleMutation.mutate({
                      docId: doc.id,
                      useAsExample: !doc.isExample
                    })}
                    disabled={!canWrite || doc.status !== DocumentProgressStatusEnum.DONE || toggleExampleMutation.isLoading}
                    className="sr-only peer"
                  />
                  <div className={`w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-brand-yellow-500 ${!canWrite ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                </label>
              </div>
              )}

              {/* Status */}
              <div className="flex items-center justify-between mb-3">
                <StatusIndicator status={doc.status} showPulse={doc.status === DocumentProgressStatusEnum.IN_PROGRESS} />
                {doc.status === DocumentProgressStatusEnum.IN_PROGRESS && (
                  <span className="text-xs text-gray-500">Processing...</span>
                )}
              </div>

              {/* Failure reason (e.g. out of credits) */}
              {doc.status === DocumentProgressStatusEnum.FAILED && doc.remarks && (
                <div className="mb-3 flex items-start gap-1.5 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-2.5 py-2">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span className="break-words">{doc.remarks}</span>
                </div>
              )}

              {/* AI-generated summary. Collapsed to 3 lines; expands to the FULL
                  text (no truncation) so the whole summary is readable in place. */}
              {doc.summary && (
                <div className="mb-3 rounded-lg border border-blue-100 bg-blue-50/60 px-2.5 py-2">
                  <div className="flex items-center gap-1.5 mb-1">
                    <FileText className="w-3.5 h-3.5 shrink-0 text-blue-600" />
                    <span className="text-xs font-semibold text-blue-900">Summary</span>
                    <button
                      type="button"
                      onClick={() => copySummary(doc.id, doc.summary)}
                      className="ml-auto shrink-0 rounded p-1 text-blue-600 hover:bg-blue-100 hover:text-blue-800"
                      title="Copy summary"
                      aria-label="Copy summary"
                    >
                      {copiedSummaryId === doc.id ? (
                        <Check className="h-3.5 w-3.5 text-green-600" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                  <p
                    className={`text-xs text-gray-700 whitespace-pre-wrap break-words ${
                      expandedSummaries.has(doc.id) ? '' : 'line-clamp-3'
                    }`}
                  >
                    {doc.summary}
                  </p>
                  <button
                    type="button"
                    onClick={() => toggleSummary(doc.id)}
                    className="mt-1 text-xs font-medium text-blue-700 hover:text-blue-900 hover:underline"
                  >
                    {expandedSummaries.has(doc.id) ? 'Show less' : 'Show full summary'}
                  </button>

                  {doc.pageCount ? (
                    <div className="mt-2 border-t border-blue-100 pt-2 text-[10px] text-gray-500">
                      {doc.pageCount} {doc.pageCount === 1 ? 'page' : 'pages'}
                    </div>
                  ) : null}
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between pt-3 border-t">
                <div className="text-xs text-gray-500 min-w-0 flex-1">
                  <p className="flex items-center gap-1 truncate">
                    <User className="w-3 h-3 shrink-0" />
                    <span className="truncate" title={doc.uploadedBy}>
                      {doc.uploadedBy}
                    </span>
                  </p>
                  <p className="flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3 shrink-0" /> {doc.uploadedAt}
                  </p>
                </div>

                <div className="flex gap-1 shrink-0 ml-2">
                  {/* Preview */}
                  {supportsPreview(doc.type) && (
                    <button
                      onClick={() => {
                        if (!canWrite) return;

                        const docUrl = store.document_urls.find(
                          (d) => d._id === doc.id
                        );
                        if (docUrl) {
                          setSelectedDocument(docUrl);
                          setShowPreviewModal(true);
                        }
                      }}
                      disabled={!canWrite}
                      title={!canWrite ? "You do not have permission" : "Preview"}
                      className={`p-1.5 rounded transition ${
                        canWrite
                          ? "text-gray-400 hover:text-brand-blue-600 hover:bg-brand-blue-50 cursor-pointer"
                          : "text-gray-300 cursor-not-allowed opacity-60"
                      }`}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  )}

                  {/* Download */}
                  <button
                    onClick={() => {
                      if (!canWrite) return;

                      if (doc.key && doc.name) {
                        handleDownloadFile(doc.key, doc.name, doc.id);
                      } else {
                        toast.error("Document not available");
                      }
                    }}
                    disabled={!canWrite}
                    title={!canWrite ? "You do not have permission" : "Download"}
                    className={`p-1.5 rounded transition ${
                      canWrite
                        ? "text-gray-400 hover:text-brand-green-600 hover:bg-brand-green-50 cursor-pointer"
                        : "text-gray-300 cursor-not-allowed opacity-60"
                    }`}
                  >
                    <Download className="w-4 h-4" />
                  </button>

                  {/* Delete */}
                  <button
                    onClick={() => {
                      if (!canWrite) return;

                      setDocumentToDelete({ id: doc.id, name: doc.name });
                      setShowDeleteModal(true);
                    }}
                    disabled={!canWrite || deleteDocumentMutation.isLoading}
                    title={
                      !canWrite
                        ? "You do not have permission"
                        : deleteDocumentMutation.isLoading
                        ? "Deleting..."
                        : "Delete"
                    }
                    className={`p-1.5 rounded transition ${
                      !canWrite || deleteDocumentMutation.isLoading
                        ? "text-gray-300 cursor-not-allowed opacity-60"
                        : "text-gray-400 hover:text-red-600 hover:bg-red-50 cursor-pointer"
                    }`}
                  >
                    {deleteDocumentMutation.isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Document
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Example
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Uploaded
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {documents.map((doc) => (
                <React.Fragment key={doc.id}>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {getFileIcon(doc.type)}
                      <div>
                        <p className="font-medium text-gray-900">{doc.name}</p>
                        <p className="text-sm text-gray-500">{doc.size}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusIndicator
                      status={doc.status}
                      showPulse={doc.status === DocumentProgressStatusEnum.IN_PROGRESS}
                    />
                    {doc.status === DocumentProgressStatusEnum.FAILED && doc.remarks && (
                      <p className="mt-1 flex items-start gap-1 text-xs text-red-600 max-w-[220px]" title={doc.remarks}>
                        <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                        <span className="truncate">{doc.remarks}</span>
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {!['csv', 'txt', 'mp3', 'wav', 'm4a'].includes(doc.type) ? (
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={doc.isExample}
                        onChange={() => toggleExampleMutation.mutate({
                          docId: doc.id,
                          useAsExample: !doc.isExample
                        })}
                        disabled={!canWrite || toggleExampleMutation.isLoading}
                        className="sr-only peer"
                      />
                      <div className={`w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-brand-yellow-500 ${!canWrite ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                    </label>
                    ) : (
                      <span className="text-xs text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900">{doc.uploadedBy}</div>
                    <div className="text-xs text-gray-500">{doc.uploadedAt}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      {supportsPreview(doc.type) && (
                        <button
                          onClick={() => {
                            const docUrl = store.document_urls.find(d => d._id === doc.id);
                            if (docUrl) {
                              setSelectedDocument(docUrl);
                              setShowPreviewModal(true);
                            }
                          }}
                          className="p-1.5 text-gray-400 hover:text-brand-blue-600 hover:bg-brand-blue-50 rounded"
                          title="Preview"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (doc.key && doc.name) {
                            handleDownloadFile(doc.key, doc.name, doc.id);
                          } else {
                            toast.error('Document not available');
                          }
                        }}
                        className="p-1.5 text-gray-400 hover:text-brand-green-600 hover:bg-brand-green-50 rounded"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setDocumentToDelete({ id: doc.id, name: doc.name });
                          setShowDeleteModal(true);
                        }}
                        disabled={deleteDocumentMutation.isLoading}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed" 
                        title="Delete"
                      >
                        {deleteDocumentMutation.isLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>

                {/* AI summary on its own full-width row — a table cell would squeeze
                    a multi-paragraph summary. Collapsed to 2 lines; expands to the
                    COMPLETE text. */}
                {doc.summary && (
                  <tr className="bg-blue-50/40">
                    <td colSpan={5} className="px-4 pb-3 pt-0">
                      <div className="flex items-start gap-2">
                        <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-600" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-semibold text-blue-900">Summary</span>
                            <button
                              type="button"
                              onClick={() => copySummary(doc.id, doc.summary)}
                              className="rounded p-0.5 text-blue-600 hover:bg-blue-100 hover:text-blue-800"
                              title="Copy summary"
                              aria-label="Copy summary"
                            >
                              {copiedSummaryId === doc.id ? (
                                <Check className="h-3.5 w-3.5 text-green-600" />
                              ) : (
                                <Copy className="h-3.5 w-3.5" />
                              )}
                            </button>
                          </div>
                          <p
                            className={`mt-0.5 whitespace-pre-wrap break-words text-xs text-gray-700 ${
                              expandedSummaries.has(doc.id) ? '' : 'line-clamp-2'
                            }`}
                          >
                            {doc.summary}
                          </p>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={() => toggleSummary(doc.id)}
                              className="text-xs font-medium text-blue-700 hover:text-blue-900 hover:underline"
                            >
                              {expandedSummaries.has(doc.id) ? 'Show less' : 'Show full summary'}
                            </button>
                            {doc.pageCount ? (
                              <span className="text-[10px] text-gray-500">
                                {doc.pageCount} {doc.pageCount === 1 ? 'page' : 'pages'}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {documents.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">No documents found</p>
          <p className="text-sm text-gray-500">Upload documents to get started</p>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && store && (
        <UploadDocumentModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          storeId={store.id}
          storeName={store.name}
          onSuccess={() => {
            setShowUploadModal(false);
            // Smooth refresh without page reload
            queryClient.invalidateQueries({ queryKey: ['document-store', storeId] });
          }}
        />
      )}

      {/* Preview Modal */}
      <DocumentPreviewModal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setSelectedDocument(null);
        }}
        document={selectedDocument}
        storeId={store?.id}
        storeName={store?.name}
      />

      {/* AI Search Modal */}
      {store && (
        <AIDocumentSearchModal
          isOpen={showSearchModal}
          onClose={() => setShowSearchModal(false)}
          storeName={store.name}
          storeId={store.id}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDocumentToDelete(null);
        }}
        onConfirm={() => {
          if (documentToDelete) {
            deleteDocumentMutation.mutate(documentToDelete.id);
            setShowDeleteModal(false);
            setDocumentToDelete(null);
          }
        }}
        title="Delete Document"
        description="Are you sure you want to delete this document? This action cannot be undone."
        itemName={documentToDelete?.name}
        isLoading={deleteDocumentMutation.isLoading}
      />
    </div>
  );
}